/* eslint-disable max-lines */
/* eslint-disable no-restricted-syntax */
import {
  cloneElement,
  ReactElement,
  ReactNode,
  useContext,
  useState
} from 'react';
import cn from 'classnames';
import { ThemeContext } from '../../../utils/providers/theme-context';
import { Tooltip } from './tooltip';
import Loader from '../loaders/loader';

export type ButtonGroupSize = 'small' | 'medium' | 'large';
export type ButtonGroupVariant =
  | 'outline'
  | 'smoke'
  | 'ghost'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'info'
  | 'text'
  | 'gray';

export interface ButtonGroupItem {
  icon: ReactNode;
  label?: string;
  onClick: (e: React.MouseEvent) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  tooltip?: ReactElement | string;
  disabled?: boolean;
  loading?: boolean;
  danger?: boolean;
  variant?: ButtonGroupVariant;
}

interface ButtonGroupProps {
  items: ButtonGroupItem[];
  size?: ButtonGroupSize;
  variant?: ButtonGroupVariant;
  className?: string;
}

const ButtonGroup = ({
  items,
  size = 'medium',
  variant = 'outline',
  className
}: ButtonGroupProps) => {
  const themeContext = useContext(ThemeContext);
  const theme = themeContext?.theme ?? 'light';
  const getColour = themeContext?.getColour;

  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  // Base colors from theme
  const active = getColour?.('activeText') ?? 'inherit';
  const border = getColour?.('border') ?? 'inherit';
  // Use thirdLayer (white) to stand out against secondLayer (zinc-50)
  // To revert: use getColour('secondLayer') or pick('#ffffff', '#21262d')
  const bg = getColour?.('thirdLayer') ?? 'inherit';
  const bgSmoke = getColour?.('thirdLayer') ?? 'inherit';
  const iconColor = getColour?.('icon') ?? 'inherit';

  // Helper to darken/lighten a color for hover
  const adjustBrightness = (hexColor: string, percent: number) => {
    const num = parseInt(hexColor.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      '#' +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  };

  // Get variant colors following Button.tsx patterns
  const getVariantColors = (v: ButtonGroupVariant, isDanger?: boolean) => {
    // If danger flag is set, use danger colors
    if (isDanger) {
      return {
        bg: 'transparent',
        bgHover: pick('#fef2f2', 'rgba(248, 81, 73, 0.15)'),
        fg: pick('#dc2626', '#f85149'),
        fgHover: pick('#b91c1c', '#ff6b6b'),
        border: pick('#fecaca', '#da3633')
      };
    }

    switch (v) {
      case 'primary':
        return {
          bg: 'transparent',
          bgHover: pick('rgba(37, 99, 235, 0.1)', 'rgba(88, 166, 255, 0.15)'),
          fg: active,
          fgHover: adjustBrightness(active, theme === 'dark' ? 20 : -20),
          border: active
        };
      case 'secondary':
        return {
          bg: 'transparent',
          bgHover: pick('#f3f4f6', 'rgba(107, 114, 128, 0.15)'),
          fg: pick('#4b5563', '#9ca3af'),
          fgHover: pick('#1f2937', '#e5e7eb'),
          border: pick('#9ca3af', '#4b5563')
        };
      case 'success':
        return {
          bg: 'transparent',
          bgHover: pick('#f0fdf4', 'rgba(46, 160, 67, 0.15)'),
          fg: pick('#16a34a', '#2ea043'),
          fgHover: pick('#15803d', '#3fb950'),
          border: pick('#bbf7d0', '#238636')
        };
      case 'danger':
        return {
          bg: 'transparent',
          bgHover: pick('#fef2f2', 'rgba(248, 81, 73, 0.15)'),
          fg: pick('#dc2626', '#f85149'),
          fgHover: pick('#b91c1c', '#ff6b6b'),
          border: pick('#fecaca', '#da3633')
        };
      case 'info':
        return {
          bg: 'transparent',
          bgHover: pick('#fefce8', 'rgba(234, 179, 8, 0.15)'),
          fg: pick('#ca8a04', '#eab308'),
          fgHover: pick('#a16207', '#facc15'),
          border: pick('#fef08a', '#ca8a04')
        };
      case 'text':
        return {
          bg: 'transparent',
          bgHover: pick('#f4f4f5', 'rgba(177, 186, 196, 0.12)'),
          fg: iconColor,
          fgHover: pick('#374151', '#c9d1d9'),
          border: 'transparent'
        };
      case 'gray':
        return {
          bg: 'transparent',
          bgHover: pick('#f4f4f5', 'rgba(177, 186, 196, 0.12)'),
          fg: iconColor,
          fgHover: pick('#374151', '#c9d1d9'),
          border: border
        };
      case 'smoke':
        return {
          bg: bgSmoke,
          bgHover: pick('#e4e4e7', '#30363d'),
          fg: iconColor,
          fgHover: active,
          border: border
        };
      case 'ghost':
        return {
          bg: 'transparent',
          bgHover: pick('#f4f4f5', 'rgba(177, 186, 196, 0.12)'),
          fg: iconColor,
          fgHover: active,
          border: 'transparent'
        };
      case 'outline':
      default:
        return {
          bg: bg,
          bgHover: pick('#f4f4f5', '#30363d'),
          fg: iconColor,
          fgHover: active,
          border: border
        };
    }
  };

  // Fixed widths for consistent button sizing (icon-only buttons)
  const getSizeConfig = () => {
    switch (size) {
      case 'small':
        return {
          height: 'h-6',
          width: 'w-7',
          iconSize: 12,
          dividerHeight: 'h-4',
          textSize: 'text-xs',
          px: 'px-2',
          gap: 'gap-1'
        };
      case 'large':
        return {
          height: 'h-9',
          width: 'w-10',
          iconSize: 16,
          dividerHeight: 'h-6',
          textSize: 'text-sm',
          px: 'px-3',
          gap: 'gap-1.5'
        };
      case 'medium':
      default:
        return {
          height: 'h-8',
          width: 'w-9',
          iconSize: 14,
          dividerHeight: 'h-5',
          textSize: 'text-xs',
          px: 'px-2.5',
          gap: 'gap-1'
        };
    }
  };

  const sizeConfig = getSizeConfig();

  // Container uses theme colors for border (not variant-specific)
  const containerStyles = {
    backgroundColor: bg,
    borderColor: border
  };

  const ButtonWithHover = ({
    item,
    index
  }: {
    item: ButtonGroupItem;
    index: number;
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const itemVariant = item.variant || variant;
    const colors = getVariantColors(itemVariant, item.danger);
    const hasLabel = !!item.label;

    const currentFg =
      isHovered && !item.disabled && !item.loading ? colors.fgHover : colors.fg;

    // Create radial gradient that fades from center to edges
    const hoverGradient = `radial-gradient(ellipse at center, ${colors.bgHover} 0%, transparent 100%)`;
    const backgroundStyle =
      isHovered && !item.disabled && !item.loading
        ? { background: hoverGradient }
        : { backgroundColor: 'transparent' };

    const button = (
      <button
        onClick={item.onClick}
        onMouseDown={item.onMouseDown}
        disabled={item.disabled || item.loading}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cn(
          sizeConfig.height,
          hasLabel ? sizeConfig.px : sizeConfig.width,
          'flex items-center justify-center transition-all duration-200 cursor-pointer',
          hasLabel && sizeConfig.gap,
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
          !item.disabled && !item.loading && 'active:scale-95'
        )}
        style={{
          color: currentFg,
          ...backgroundStyle
        }}
      >
        {item.loading ? (
          <Loader width={1} height={sizeConfig.iconSize} color={colors.fg} />
        ) : (
          <>
            <span
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: sizeConfig.iconSize,
                height: sizeConfig.iconSize
              }}
            >
              {typeof item.icon === 'object' &&
              item.icon !== null &&
              'type' in item.icon
                ? cloneElement(item.icon as ReactElement, {
                    size: sizeConfig.iconSize,
                    width: sizeConfig.iconSize,
                    height: sizeConfig.iconSize
                  })
                : item.icon}
            </span>
            {hasLabel && (
              <span
                className={cn(
                  sizeConfig.textSize,
                  'font-medium whitespace-nowrap'
                )}
              >
                {item.label}
              </span>
            )}
          </>
        )}
      </button>
    );

    if (item.tooltip) {
      return (
        <Tooltip key={index} label={item.tooltip}>
          {button}
        </Tooltip>
      );
    }

    return <span key={index}>{button}</span>;
  };

  const renderButton = (item: ButtonGroupItem, index: number) => {
    return <ButtonWithHover key={index} item={item} index={index} />;
  };

  return (
    <div
      className={cn(
        'inline-flex items-center rounded-2xl border overflow-hidden shadow-[0_4px_12px_2px_rgba(0,0,0,0.10),0_2px_4px_rgba(0,0,0,0.06)]',
        className
      )}
      style={containerStyles}
    >
      {items.map((item, index) => (
        <div key={index} className="flex items-center">
          {renderButton(item, index)}
          {index < items.length - 1 && (
            <div
              className={cn(sizeConfig.dividerHeight, 'w-px vu')}
              style={{ backgroundColor: border }}
            />
          )}
        </div>
      ))}
    </div>
  );
};

ButtonGroup.displayName = 'ButtonGroup';

export default ButtonGroup;
